<template>
  <div class="user-profile--notes-list">
    <v-row>
      <v-col cols="12">
        <v-row class="mb-4" align="center">
          <v-col>
            <h2>Notes</h2>
          </v-col>
          <v-col class="d-flex justify-end">
            <v-btn class="bg-primary-gradient primary" @click="openAddDialog">
              <v-icon class="mr-2">{{ icons.add }}</v-icon>
              Add New Note
            </v-btn>
          </v-col>
        </v-row>

        <template v-if="loading">
          <v-skeleton-loader
            v-for="n in 3"
            :key="n"
            type="list-item-three-line"
            class="mb-2"
          />
        </template>

        <template v-else-if="hasNotes">
          <div
            v-for="note in notes"
            :key="note.id"
            class="note-card clickable"
            @click="openViewDialog(note)"
          >
            <div class="note-card__body">
              <div class="note-card__info">
                {{ formatDate(note.created_at) }} - added by
                {{ note.admin_name }}
              </div>
              <div class="note-card__content">
                {{ note.content }}
              </div>
            </div>
            <div class="note-card__action">
              <v-btn icon @click.stop="openEditDialog(note)">
                <v-icon>{{ icons.edit }}</v-icon>
              </v-btn>
            </div>
          </div>
        </template>

        <no-list v-else details="No Notes" />
      </v-col>
    </v-row>

    <add-note-dialog
      v-model="showAddDialog"
      :note="editingNote"
      :user-id="userId"
      @saved="fetchNotes"
    />

    <view-note-dialog
      v-model="showViewDialog"
      :note="viewingNote"
      @deleted="onNoteDeleted"
    />

    <v-toast ref="toast" />
  </div>
</template>

<script>
import NoList from '@/components/elements/NoList'
import VToast from '@/components/elements/Toast'
import AddNoteDialog from '../components/AddNoteDialog'
import ViewNoteDialog from '../components/ViewNoteDialog'
import { mdiPlus, mdiPencilOutline } from '@mdi/js'
import { mapActions, mapState, mapMutations } from 'vuex'
import dayjs from 'dayjs'

export default {
  name: 'UserNotes',

  components: {
    NoList,
    VToast,
    AddNoteDialog,
    ViewNoteDialog,
  },

  data() {
    return {
      loading: false,
      showAddDialog: false,
      showViewDialog: false,
      editingNote: null,
      viewingNote: null,
      icons: {
        add: mdiPlus,
        edit: mdiPencilOutline,
      },
    }
  },

  created() {
    this.fetchNotes()
  },

  destroyed() {
    this.clearNotes()
  },

  computed: {
    ...mapState({
      notes: (state) => state.userProfile.notes,
    }),

    userId() {
      return this.$route.params.id
    },

    hasNotes() {
      return !!this.notes.length
    },
  },

  methods: {
    ...mapActions({
      getNotes: 'userProfile/getNotes',
      deleteNote: 'userProfile/deleteNote',
    }),
    ...mapMutations({
      clearNotes: 'userProfile/clearNotes',
    }),

    async fetchNotes() {
      this.loading = true
      await this.getNotes(this.userId)
      await this.$sleep()
      this.loading = false
    },

    formatDate(date) {
      if (!date) return ''
      return dayjs(date).format('DD - MM - YYYY, h:mm a')
    },

    openAddDialog() {
      this.editingNote = null
      this.showAddDialog = true
    },

    openEditDialog(note) {
      this.editingNote = note
      this.showAddDialog = true
    },

    openViewDialog(note) {
      this.viewingNote = note
      this.showViewDialog = true
    },

    async onNoteDeleted(note) {
      try {
        await this.deleteNote({ userId: this.userId, noteId: note.id })
        this.showViewDialog = false
        this.$refs.toast.open({
          color: 'success',
          message: 'Note deleted successfully.',
        })
        this.fetchNotes()
      } catch (err) {
        this.$refs.toast.open({
          color: 'error',
          message: 'Failed to delete note.',
        })
      }
    },
  },
}
</script>

<style scoped>
.note-card {
  display: flex;
  align-items: center;
  background-color: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: box-shadow 0.2s;
}

.note-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.note-card__body {
  flex: 1;
  min-width: 0;
}

.note-card__info {
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
}

.note-card__content {
  font-size: 15px;
  font-weight: 500;
  color: #111;
  white-space: pre-wrap;
  word-break: break-word;
}

.note-card__action {
  flex-shrink: 0;
  margin-left: 16px;
}
</style>
